import apiClient from '../lib/api-client';
import { convertFiltersToServerFormat, type ServerFilter } from '../lib/utils';
import { getAdminListSortCriteria } from '../lib/admin-list-sort';
import type { 
  School, 
  SchoolsResponse, 
  SchoolFilters, 
  SchoolCreateRequest, 
  SchoolUpdateRequest,
  ServerFiltersParams
} from '../modules/school/types';

export interface GetSchoolsParams extends SchoolFilters {
  page?: number;
  per_page?: number;
}

export interface SchoolTeachersResponse {
  success: boolean;
  data: any[];
  message: string;
  pagination?: {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

export interface SchoolStudentsResponse {
  success: boolean;
  data: any[];
  message: string;
  pagination?: {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

class SchoolService {
  private readonly baseUrl = '/school';

  private convertFiltersToServerFormat(filters: SchoolFilters): ServerFilter[] {
    const fieldMappings = {
      type: { field: 'type', operator: 'eq' as const },
      level: { field: 'level', operator: 'eq' as const },
      status: { field: 'status', operator: 'eq' as const },
      search: { field: 'name', operator: 'like' as const }
    };

    return convertFiltersToServerFormat(filters, fieldMappings);
  }

  async getSchools(params: GetSchoolsParams = {}): Promise<SchoolsResponse> {
    const { type, status, search, sort, page, per_page, ...otherParams } = params;
    
    // Convert client filters to server format
    const filters = this.convertFiltersToServerFormat({ type, status, search });
    
    const serverParams: ServerFiltersParams = {
      ...otherParams,
      sort: getAdminListSortCriteria(sort),
    };

    // Chỉ thêm filters nếu có filter nào được set
    if (filters.length > 0) {
      serverParams.filters = filters;
    }

    // Backend đọc pagination lồng (`criteria.pagination.page`), gửi phẳng sẽ bị
    // bỏ qua và API trả về toàn bộ danh sách. Xem BaseRepository::search.
    if (page !== undefined && per_page !== undefined) {
      serverParams.pagination = { page, per_page };
    }

    const response = await apiClient.post(`${this.baseUrl}/search`, serverParams);
    return response.data;
  }

  async getSchool(id: string): Promise<School> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data;
  }

  async getSchoolTeachers(
    schoolIdOrFilters?: string | Record<string, any>, 
    searchParam?: string
  ): Promise<SchoolTeachersResponse> {
    let endpoint: string;
    let params: any = {};
    
    // Handle different call signatures
    if (typeof schoolIdOrFilters === 'string') {
      // Called with (schoolId, search)
      endpoint = schoolIdOrFilters 
        ? `/schooluser/${schoolIdOrFilters}/teachers` 
        : '/schooluser/teachers';
      if (searchParam) {
        params.search = searchParam;
      }
    } else if (typeof schoolIdOrFilters === 'object') {
      // Called with (filters)
      const filters = schoolIdOrFilters || {};
      endpoint = filters.schoolId 
        ? `/schooluser/${filters.schoolId}/teachers` 
        : '/schooluser/teachers';
      const { sort, ...requestFilters } = filters;
      const sortCriteria = getAdminListSortCriteria(sort);
      params = {
        ...requestFilters,
        sort_by: sortCriteria.field,
        sort_order: sortCriteria.direction,
      };
      delete params.schoolId; // Remove schoolId from params as it's in the URL
    } else {
      // Called with no arguments
      endpoint = '/schooluser/teachers';
    }
    
    const response = await apiClient.get(endpoint, { params });
    return response.data;
  }

  async getSchoolStudents(filters: any = {}): Promise<SchoolStudentsResponse> {
    const { sort, ...requestFilters } = filters;
    const sortCriteria = getAdminListSortCriteria(sort);
    const response = await apiClient.get('/schooluser/students', {
      params: {
        ...requestFilters,
        sort_by: sortCriteria.field,
        sort_order: sortCriteria.direction,
      }
    });
    return response.data;
  }

  async getSchoolStudent(id: string): Promise<any> {
    const response = await apiClient.get(`/schooluser/students/${id}`);
    return response.data.data;
  }

  async createSchoolStudent(data: any): Promise<any> {
    const response = await apiClient.post('/schooluser/students', data);
    return response.data;
  }

  async updateSchoolStudent(id: string, data: any): Promise<any> {
    const response = await apiClient.put(`/schooluser/students/${id}`, data);
    return response.data;
  }

  async deleteSchoolStudent(id: string): Promise<any> {
    const response = await apiClient.delete(`/schooluser/students/${id}`);
    return response.data;
  }

  async toggleSchoolStudentStatus(id: string, status: string): Promise<any> {
    const response = await apiClient.patch(`/schooluser/students/${id}/status`, { status });
    return response.data;
  }

  async createSchool(data: SchoolCreateRequest): Promise<School> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data;
  }

  async updateSchool(id: string, data: SchoolUpdateRequest): Promise<School> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  async deleteSchool(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  // Backend không có route toggle-status riêng
  async toggleSchoolStatus(id: string, status: 'active' | 'inactive'): Promise<void> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, { status });
    // Không tìm thấy id thì API vẫn trả 200 kèm data=false
    if (response.data?.data === false) {
      throw new Error('Không tìm thấy trường học');
    }
  }

  async bulkImportSchools(schools: SchoolCreateRequest[]): Promise<{
    success: boolean;
    data: {
      success_count: number;
      error_count: number;
      errors: Array<{
        row: number;
        message: string;
        data: any;
      }>;
      created_schools: School[];
    };
    message: string;
  }> {
    const response = await apiClient.post(`${this.baseUrl}/bulk-import`, { schools });
    return response.data;
  }

  // Teacher CRUD operations
  async createSchoolTeacher(data: any): Promise<any> {
    const response = await apiClient.post('/schooluser/teachers', data);
    return response.data;
  }

  async getSchoolTeacher(id: string): Promise<any> {
    const response = await apiClient.get(`/schooluser/teachers/${id}`);
    return response.data.data;
  }

  async updateSchoolTeacher(id: string, data: any): Promise<any> {
    const response = await apiClient.put(`/schooluser/teachers/${id}`, data);
    return response.data.data;
  }

  async deleteSchoolTeacher(id: string): Promise<any> {
    const response = await apiClient.delete(`/schooluser/teachers/${id}`);
    return response.data;
  }

  async toggleSchoolTeacherStatus(id: string, status: string): Promise<any> {
    const response = await apiClient.patch(`/schooluser/teachers/${id}/status`, { status });
    return response.data.data;
  }
}

export const schoolService = new SchoolService();
export default schoolService;
